import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectLabel,
  SelectGroup,
} from "../../../components/ui/select";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../App";
import {
  ZohoInvoice,
  ZohoInvoiceFormData,
  SITE_CODE_OPTIONS,
  TYPE_OPTIONS,
  QUARTER_OPTIONS,
  STATUS_OPTIONS,
  normalizeType,
} from "../types/zoho";
import {
  Plus,
  Search,
  Trash2,
  Edit2,
  FileText,
  AlertCircle,
  X,
  Loader2,
  Calendar,
  DollarSign,
} from "lucide-react";
import { ZohoInvoiceSyncReviewModal } from "./SyncReviewModal";

export const ZohoInvoicesPage = () => {
  const [invoices, setInvoices] = useState<ZohoInvoice[]>([]);
  const [allInvoices, setAllInvoices] = useState<ZohoInvoice[]>([]);
  const [totalInvoices, setTotalInvoices] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null);
  const [editingZohoId, setEditingZohoId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [syncMetadata, setSyncMetadata] = useState<any>(null);
  const [lastSyncType, setLastSyncType] = useState<"FULL" | "INCREMENTAL" | null>(null);
  const [isSyncReviewModalOpen, setIsSyncReviewModalOpen] = useState(false);
  const [fetchedInvoices, setFetchedInvoices] = useState<any[]>([]);

  const [formData, setFormData] = useState<ZohoInvoiceFormData>({
    zohoId: "",
    siteCode: "",
    type: "",
    invoiceNo: "",
    invoiceDate: "",
    poNo: "",
    paymentDueDate: "",
    quarter: "",
    amount: "",
    status: "",
  });

  const { authToken = "", id = "" } = useSelector(
    (state: any) => state.loginData || {}
  );

  const maxResults = 10;

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.siteCode.trim()) newErrors.siteCode = "Site code is required";
    if (!formData.type.trim()) newErrors.type = "Type is required";
    if (!formData.invoiceNo.trim()) newErrors.invoiceNo = "Invoice number is required";
    if (!formData.invoiceDate.trim()) newErrors.invoiceDate = "Invoice date is required";
    if (!formData.poNo.trim()) newErrors.poNo = "PO number is required";
    if (!formData.paymentDueDate.trim()) newErrors.paymentDueDate = "Payment due date is required";
    if (!formData.quarter.trim()) newErrors.quarter = "Quarter is required";
    if (!formData.amount.trim()) newErrors.amount = "Amount is required";
    if (!formData.status.trim()) newErrors.status = "Status is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fetch invoices from API
  const fetchAllInvoicesForSearch = async () => {
    if (!authToken || !id) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}account/${id}/zoho-invoices?page=1&limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setAllInvoices(response.data.invoiceRecords || []);
      setTotalInvoices(response.data.totalCount || 0);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      alert("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  // Get paginated records
  const getPaginatedRecords = (recordsToDisplay: ZohoInvoice[]) => {
    const startIndex = (page - 1) * maxResults;
    const endIndex = startIndex + maxResults;
    return recordsToDisplay.slice(startIndex, endIndex);
  };

  // Fetch Zoho invoices from Zoho Books API (with sync status feedback)
  const handleFetchFromZoho = async () => {
    if (!authToken || !id) return;

    setIsFetching(true);
    try {
      // Step 1: Fetch ALL invoices from Zoho (no conditions)
      const response = await axios.post(
        `${API_BASE_URL}account/${id}/zoho-invoices/fetch`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      // Step 2: Display review modal BEFORE database sync
      setFetchedInvoices(response.data.records || []);
      setIsSyncReviewModalOpen(true);
    } catch (error: any) {
      console.error("Error fetching from Zoho:", error);
      alert(error.response?.data?.message || "Failed to fetch invoices from Zoho Books");
    } finally {
      setIsFetching(false);
    }
  };

  // Called after user confirms sync in review modal
  const handleSyncConfirmed = async () => {
    try {
      // Refresh invoices list after successful sync
      await fetchAllInvoicesForSearch();
    } catch (error) {
      console.error("Error refreshing invoices:", error);
    }
  };

  // Handle invoice creation
  const handleCreateInvoice = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (editingInvoiceId) {
        // Update
        await axios.put(
          `${API_BASE_URL}account/${id}/zoho-invoices/${editingInvoiceId}/update`,
          {...formData, invoiceId: editingInvoiceId, zohoId: editingZohoId},
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        alert("Invoice updated successfully");
      } else {
        // Create - Note: Creation typically happens via Zoho fetch, but keeping for completeness
        await axios.post(
          `${API_BASE_URL}account/${id}/zoho-invoices`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        alert("Invoice created successfully");
      }

      resetForm();
      closeDrawer();
      fetchAllInvoicesForSearch();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || "Failed to save invoice";
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Handle invoice deletion
  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;

    try {
      await axios.delete(
        `${API_BASE_URL}account/${id}/zoho-invoices/${invoiceId}/delete`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      alert("Invoice deleted successfully");
      fetchAllInvoicesForSearch();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || "Failed to delete invoice";
      alert(errorMsg);
    }
  };

  // Handle edit invoice
  const handleEditInvoice = (invoice: ZohoInvoice) => {
    // Helper function to format date to YYYY-MM-DD format
    const formatDateToInput = (dateString: string | null | undefined): string => {
      if (!dateString) return "";
      
      // If already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      
      // Try to parse and format the date
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      } catch (e) {
        // If parsing fails, return empty string
      }
      
      return "";
    };

    setEditingInvoiceId(invoice.invoiceId);
    setEditingZohoId(invoice.zohoId);
    setIsCreating(false);
    setFormData({
      zohoId: invoice.zohoId,
      siteCode: invoice.siteCode,
      type: normalizeType(invoice.type),
      invoiceNo: invoice.invoiceNo,
      invoiceDate: formatDateToInput(invoice.invoiceDate),
      poNo: invoice.poNo,
      paymentDueDate: formatDateToInput(invoice.paymentDueDate),
      quarter: invoice.quarter,
      amount: invoice.amount,
      status: invoice.status,
    });
    setIsDrawerOpen(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      zohoId: "",
      siteCode: "",
      type: "",
      invoiceNo: "",
      invoiceDate: "",
      poNo: "",
      paymentDueDate: "",
      quarter: "",
      amount: "",
      status: "",
    });
    setErrors({});
    setEditingInvoiceId(null);
    setEditingZohoId(null);
    setIsCreating(false);
  };

  // Close drawer
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    resetForm();
  };

  // Filter invoices based on search term - search across all records
  const filteredInvoices = getPaginatedRecords(
    allInvoices.filter(
      (invoice) =>
        invoice.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.siteCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.poNo.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Get total filtered count for pagination
  const filteredTotalCount = allInvoices.filter(
    (invoice) =>
      invoice.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.siteCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.poNo.toLowerCase().includes(searchTerm.toLowerCase())
  ).length;

  useEffect(() => {
    fetchAllInvoicesForSearch();
  }, [authToken, id]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Note: Auto-polling removed - now handled by backend sync worker
  // Backend runs incremental sync at configurable intervals (default: 5 minutes)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-lg shadow-lg">
              <FileText className="text-white" size={28} />
            </div>
            <span>Zoho Invoices</span>
          </h1>
          <p className="text-gray-600 mt-2">
            Manage invoices synced from Zoho Books
          </p>
          {syncMetadata && (
            <div className="mt-3 flex items-center gap-3 text-sm">
              <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full font-medium">
                {lastSyncType === "FULL" ? "🔄 Full Sync" : "⚡ Incremental Sync"}
              </span>
              <span className="text-gray-600">
                Synced: {syncMetadata.synced} | Failed: {syncMetadata.failed}
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleFetchFromZoho}
            disabled={isFetching}
            className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
          >
            {isFetching ? (
              <>
                <Loader2 size={18} className="animate-spin mr-2" />
                Syncing...
              </>
            ) : (
              <>
                <Plus size={18} className="mr-2" />
                Sync from Zoho
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-3 text-gray-400" size={20} />
        <Input
          placeholder="Search by invoice no, site code, or PO number..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          className="pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Invoices List */}
      <div className="space-y-4">
        {loading && filteredInvoices.length === 0 ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="animate-spin text-teal-600" size={32} />
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
            <FileText className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600 text-lg font-medium">No invoices found</p>
            <p className="text-gray-500 mt-2">
              {searchTerm ? "Try adjusting your search" : "Click 'Sync from Zoho' to fetch invoices"}
            </p>
          </div>
        ) : (
          filteredInvoices.map((invoice) => (
            <div
              key={invoice.invoiceId}
              className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all duration-300 hover:border-blue-300"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {/* Left Section - Invoice Info */}
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-lg">
                    <FileText className="text-teal-600" size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {invoice.invoiceNo}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {invoice.siteCode}
                    </p>
                  </div>
                </div>

                {/* Right Section - Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditInvoice(invoice)}
                    className="px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-all flex items-center justify-center gap-2 font-medium"
                  >
                    <Edit2 size={16} />
                    <span className="hidden sm:inline">Edit</span>
                  </button>
                  <button
                    onClick={() => handleDeleteInvoice(invoice.invoiceId)}
                    className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-all flex items-center justify-center gap-2 font-medium"
                  >
                    <Trash2 size={16} />
                    <span className="hidden sm:inline">Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {filteredTotalCount > 0 && (
        <div className="mt-6 flex justify-between items-center">
          <span className="text-sm text-gray-600">
            Showing {Math.max(1, (page - 1) * maxResults + 1)} to {Math.min(page * maxResults, filteredTotalCount)} of{" "}
            {filteredTotalCount} invoices
          </span>
          <div className="flex gap-2">
            <Button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              variant="outline"
            >
              Previous
            </Button>
            <Button
              onClick={() => setPage(page + 1)}
              disabled={page * maxResults >= filteredTotalCount}
              variant="outline"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Edit Invoice Drawer */}
      {isDrawerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full md:max-w-2xl bg-white rounded-t-2xl md:rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-cyan-50">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingInvoiceId ? "Edit Invoice" : "Create New Invoice"}
              </h2>
              <button
                onClick={closeDrawer}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} className="text-gray-600" />
              </button>
            </div>

            {/* Form Content */}
            <div className="p-6 space-y-6">
              {/* Site Code */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Site Code
                </Label>
                <Input
                  value={formData.siteCode}
                  onChange={(e) =>
                    setFormData({ ...formData, siteCode: e.target.value })
                  }
                  className={`border ${
                    errors.siteCode ? "border-red-500" : "border-gray-300"
                  } rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="e.g., A428"
                />
                {errors.siteCode && (
                  <p className="text-red-500 text-sm mt-1">{errors.siteCode}</p>
                )}
              </div>

              {/* Type */}
              <div>
                <Label className="text-gray-700 bg-white font-semibold mb-2 block">
                  Type
                </Label>
                <Select value={formData.type || ""} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel className="bg-white">Type</SelectLabel>
                      {TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {errors.type && (
                  <p className="text-red-500 text-sm mt-1">{errors.type}</p>
                )}
              </div>

              {/* Invoice Number */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Invoice Number
                </Label>
                <Input
                  value={formData.invoiceNo}
                  onChange={(e) =>
                    setFormData({ ...formData, invoiceNo: e.target.value })
                  }
                  className={`border ${
                    errors.invoiceNo ? "border-red-500" : "border-gray-300"
                  } rounded-lg px-4 py-2 w-full`}
                  placeholder="INV-20260210-001"
                />
                {errors.invoiceNo && (
                  <p className="text-red-500 text-sm mt-1">{errors.invoiceNo}</p>
                )}
              </div>

              {/* Invoice Date */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Invoice Date
                </Label>
                <Input
                  type="date"
                  value={formData.invoiceDate}
                  onChange={(e) =>
                    setFormData({ ...formData, invoiceDate: e.target.value })
                  }
                  className={`border ${
                    errors.invoiceDate ? "border-red-500" : "border-gray-300"
                  } rounded-lg px-4 py-2 w-full`}
                />
                {errors.invoiceDate && (
                  <p className="text-red-500 text-sm mt-1">{errors.invoiceDate}</p>
                )}
              </div>

              {/* PO Number */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  PO Number
                </Label>
                <Input
                  value={formData.poNo}
                  onChange={(e) =>
                    setFormData({ ...formData, poNo: e.target.value })
                  }
                  className={`border ${
                    errors.poNo ? "border-red-500" : "border-gray-300"
                  } rounded-lg px-4 py-2 w-full`}
                  placeholder="PO-123456"
                />
                {errors.poNo && (
                  <p className="text-red-500 text-sm mt-1">{errors.poNo}</p>
                )}
              </div>

              {/* Payment Due Date */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Payment Due Date
                </Label>
                <Input
                  type="date"
                  value={formData.paymentDueDate}
                  onChange={(e) =>
                    setFormData({ ...formData, paymentDueDate: e.target.value })
                  }
                  className={`border ${
                    errors.paymentDueDate ? "border-red-500" : "border-gray-300"
                  } rounded-lg px-4 py-2 w-full`}
                />
                {errors.paymentDueDate && (
                  <p className="text-red-500 text-sm mt-1">{errors.paymentDueDate}</p>
                )}
              </div>

              {/* Quarter */}
              <div>
                <Label className="text-gray-700 bg-white font-semibold mb-2 block">
                  Quarter
                </Label>
                <Select value={formData.quarter} onValueChange={(value) => setFormData({ ...formData, quarter: value })}>
                  <SelectTrigger className="border border-gray-300 rounded-lg">
                    <SelectValue placeholder="Select quarter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel className="bg-white">Quarter</SelectLabel>
                      {QUARTER_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {errors.quarter && (
                  <p className="text-red-500 text-sm mt-1">{errors.quarter}</p>
                )}
              </div>

              {/* Amount */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Amount
                </Label>
                <Input
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData({ ...formData, amount: e.target.value })
                  }
                  className={`border ${
                    errors.amount ? "border-red-500" : "border-gray-300"
                  } rounded-lg px-4 py-2 w-full`}
                  placeholder="0.00"
                />
                {errors.amount && (
                  <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
                )}
              </div>

              {/* Status */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Status
                </Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger className="border border-gray-300 rounded-lg">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel className="bg-white">Status</SelectLabel>
                      {STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {errors.status && (
                  <p className="text-red-500 text-sm mt-1">{errors.status}</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 p-6 border-t border-gray-200 bg-white flex-shrink-0">
              <Button
                onClick={closeDrawer}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateInvoice}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  "Save Invoice"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Review Modal - Shows data before database sync */}
      <ZohoInvoiceSyncReviewModal
        isOpen={isSyncReviewModalOpen}
        onClose={() => {
          setIsSyncReviewModalOpen(false);
          setFetchedInvoices([]);
        }}
        fetchedInvoices={fetchedInvoices}
        authToken={authToken}
        accountId={id}
        currentUserId={id}
        onSyncConfirmed={handleSyncConfirmed}
      />
    </div>
  );
};
